import React, { useMemo, useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { LinearProgress } from '@material-ui/core';
import { isArray } from 'lodash';
import moment from 'moment';
import qs from 'querystring';

import { t } from '~/helpers';
import Header from '~/pages/RelPitStopControleAbastecimento/components/Header';
import * as S from '~/pages/RelPitStopControleAbastecimento/styles';
import api from '~/services/api';

import Table from './components/Table';

const RELATORIO_URL = 'pitstop/relatorio/recebimentoareia';

const FIELD_LIST =
  'datachegadarecebimentoareianf,codigonomeposto,' +
  'numeronfrecebimentoareianf,numeropedidorecebimentoareianf,' +
  'nomedistribuidoranfe,placactrecebimentoareianf,' +
  'quantidaderecebimentoareianf,quantidadesacosrecebimentoareianfitem,' +
  'pesototalkgrecebimentoareianfitem,nomeusuariorecebimentoareianf';

function RelPitStopRecebimentoAreia() {
  const [loading, setLoading] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [textoPesquisa, setTextoPesquisa] = useState('');
  const [lastReportParams, setLastReportParams] = useState(null);

  const header = useMemo(() => {
    const agora = moment();

    return {
      datainicio: agora.clone().format('DD/MM/YYYY 00:00:00'),
      datafim: agora.clone().format('DD/MM/YYYY 23:59:59'),
      idposto: '',
      codigonomeposto: '',
    };
  }, []);

  const validaCamposObrigatorios = useCallback((formData) => {
    if (formData.datainicio && formData.datafim) {
      if (formData.datainicio.length !== 19 || formData.datafim.length !== 19) {
        toast.error(t('s.cadastro.pitstop.periodo-deve-ser-informado'));
        return false;
      }
    } else {
      toast.error(t('s.cadastro.pitstop.periodo-deve-ser-informado'));
      return false;
    }

    if (!formData.idposto || Number(formData.idposto) <= 0) {
      toast.error(t('s.operacao.descarganf.postoobrigatorio'));
      return false;
    }

    return true;
  }, []);

  const getFiltroPesquisa = useCallback((formData) => {
    let filtro =
      `FILTRO - ${t('s.geral.opcoes.datahorainicial')}: ${
        formData.datainicio
      } | ` + `${t('s.geral.opcoes.datahorafinal')}: ${formData.datafim}`;

    if (formData.codigonomeposto) {
      filtro += ` | ${t('s.geral.postocomboio')}: ${formData.codigonomeposto}`;
    }

    return filtro;
  }, []);

  const getParams = useCallback(
    (formData) => ({
      dataInicio: moment(formData.datainicio, 'DD/MM/YYYY HH:mm:ss').format(
        'YYYY-MM-DD HH:mm:ss'
      ),
      dataFim: moment(formData.datafim, 'DD/MM/YYYY HH:mm:ss').format(
        'YYYY-MM-DD HH:mm:ss'
      ),
      idposto: Number(formData.idposto),
    }),
    []
  );

  const handleSubmitHeader = useCallback(
    async (formData) => {
      if (!validaCamposObrigatorios(formData)) {
        return;
      }

      try {
        setLoading(true);

        const params = getParams(formData);
        const filtro = getFiltroPesquisa(formData);

        setLastReportParams(params);
        setTextoPesquisa(filtro);

        const { data } = await api.get(RELATORIO_URL, {
          params,
          paramsSerializer: (param) => qs.stringify(param),
        });

        setDataTable(data || []);
      } catch (err) {
        if (isArray(err)) {
          err.map((e) => toast.error(t(e)));
        } else if (typeof err === 'string') {
          toast.error(t(err));
        } else {
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [getFiltroPesquisa, getParams, validaCamposObrigatorios]
  );

  const handleExportData = useCallback(
    async (formato) => {
      try {
        setLoading(true);

        if (!lastReportParams) {
          return;
        }

        const tituloRelatorio = t('s.relatorio.pitstoprecebimentoareia');

        const response = await api.post(
          `${RELATORIO_URL}/export`,
          {
            fieldlist: FIELD_LIST,
            relatorio: {
              custom: 'true',
              formato,
              tituloRelatorio,
              textoPesquisa,
              substituirHeader: JSON.stringify({
                datachegadarecebimentoareianf: {
                  label: t('s.operacao.turno.datahora'),
                  align: 'center',
                },
                codigonomeposto: {
                  label: t('s.geral.postocomboio'),
                  align: 'left',
                },
                numeronfrecebimentoareianf: {
                  label: t('s.relatorio.geral.notafiscal'),
                  align: 'left',
                },
                numeropedidorecebimentoareianf: {
                  label: t('s.relatorio.descarga.pedido'),
                  align: 'left',
                },
                nomedistribuidoranfe: {
                  label: t('s.geral.fornecedor'),
                  align: 'left',
                },
                placactrecebimentoareianf: {
                  label: t('s.operacao.recebimento.placactdescarga'),
                  align: 'left',
                },
                quantidaderecebimentoareianf: {
                  label: t('s.geral.pesonf'),
                  align: 'right',
                },
                quantidadesacosrecebimentoareianfitem: {
                  label: t('s.relatorio.pitstop.quantidadenf'),
                  align: 'right',
                },
                pesototalkgrecebimentoareianfitem: {
                  label: t('s.relatorio.pitstop.pesomedido'),
                  align: 'right',
                },
                nomeusuariorecebimentoareianf: {
                  label: t('s.cadastro.usuario.usuario'),
                  align: 'left',
                },
              }),
            },
          },
          {
            params: lastReportParams,
            paramsSerializer: (param) => qs.stringify(param),
            responseType: 'blob',
          }
        );

        const blob = new Blob([response.data], {
          type: response.headers['content-type'],
        });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;

        if (formato.toLowerCase() === 'excel') {
          link.download = `${tituloRelatorio}.xls`;
        } else {
          link.target = '_blank';
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        if (Array.isArray(err)) {
          err.map((e) => toast.error(t(e)));
        } else if (typeof err === 'string') {
          toast.error(t(err));
        } else {
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [lastReportParams, textoPesquisa]
  );

  return (
    <S.Wrapper>
      {loading && <LinearProgress />}
      <Header
        data={header}
        loading={loading}
        handleSubmit={handleSubmitHeader}
        canExport={dataTable.length > 0}
        onExportClick={handleExportData}
      />

      <Table rows={dataTable} />
    </S.Wrapper>
  );
}

export default RelPitStopRecebimentoAreia;